import React, { useMemo } from 'react';
import { fmtIST } from '../../../utils/datetimeIST';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TXN_TONE = {
  PAID: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  REFUNDED: 'bg-blue-50 text-blue-800 border-blue-200',
  ADJUSTMENT: 'bg-gray-50 text-gray-700 border-gray-200',
};

function buildFallbackTransactions(transactions, payment, commercial, booking) {
  if (transactions?.entries?.length || transactions?.commercial_summary?.total_collected) {
    return transactions;
  }
  const fb = commercial?.fare_breakdown || booking?.fare_breakdown || {};
  const entries = [];
  const total = Number(booking?.total_amount || commercial?.total_amount || payment?.total_amount || 0);
  const paid = Number(payment?.amount_paid || total);
  if (paid > 0 || booking?.payment_status || booking?.status === 'confirmed') {
    entries.push({
      type: 'PAID',
      channel: payment?.payment_mode || booking?.payment_mode || booking?.payment_method || 'booking',
      amount: paid || total,
      status: payment?.payment_status || booking?.payment_status || 'paid',
      reference: payment?.payment_transaction_id || booking?.payment_transaction_id || booking?.transaction_id,
      description: `Booking payment (${payment?.payment_mode || booking?.payment_mode || '—'})`,
      created_at: payment?.paid_at || booking?.paid_at || booking?.created_at,
    });
  }
  if (payment?.wallet_transaction) {
    const wt = payment.wallet_transaction;
    entries.push({
      type: 'PAID',
      channel: 'wallet',
      amount: Math.abs(Number(wt.amount || 0)),
      status: wt.status || 'completed',
      reference: wt.transaction_id,
      description: wt.description || 'Wallet debit',
      created_at: wt.created_at,
    });
  }
  return {
    entries,
    commercial_summary: {
      base_fare: fb.base_fare || fb.baseFare,
      taxes_and_fees: fb.taxes || fb.total_tax,
      supplier_total: fb.supplier_total || fb.net_fare,
      markup_amount: commercial?.markup_amount || fb.markup,
      ssr_amount: commercial?.ssr_amount || booking?.ssr_amount,
      total_collected: total,
      customer_gstin: payment?.gstin_on_booking || booking?.gstin,
    },
    total_paid: entries.filter((e) => e.type === 'PAID').reduce((s, e) => s + Number(e.amount || 0), 0),
    total_refunded: 0,
  };
}

export default function BookingTransactionsPanel({ transactions, payment, commercial, booking }) {
  const tx = useMemo(
    () => buildFallbackTransactions(transactions, payment, commercial, booking),
    [transactions, payment, commercial, booking],
  );

  const entries = tx?.entries || [];
  const cs = tx?.commercial_summary || {};

  if (!entries.length && !cs.total_collected && !commercial?.total_amount) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
        <p>No payment transactions found for this booking.</p>
        <p className="text-xs mt-1 text-gray-500">If you recently deployed hub changes, restart the backend server and hard-refresh the browser.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="hub-transactions-panel">
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <p className="text-[10px] uppercase font-bold text-gray-500 mb-3">Fare &amp; GST summary</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          {cs.base_fare != null && (
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500">Base fare</p>
              <p className="font-semibold">{fmt(cs.base_fare)}</p>
            </div>
          )}
          {cs.taxes_and_fees != null && (
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500">Taxes &amp; fees</p>
              <p className="font-semibold">{fmt(cs.taxes_and_fees)}</p>
            </div>
          )}
          {cs.supplier_total != null && (
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500">Supplier cost</p>
              <p className="font-semibold">{fmt(cs.supplier_total)}</p>
            </div>
          )}
          {(cs.markup_amount > 0) && (
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500">Markup</p>
              <p className="font-semibold">{fmt(cs.markup_amount)}</p>
            </div>
          )}
          {(cs.ssr_amount > 0) && (
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] text-gray-500">SSR / add-ons</p>
              <p className="font-semibold">{fmt(cs.ssr_amount)}</p>
            </div>
          )}
          <div className="bg-[#1B375B]/5 rounded-lg p-2 border border-[#1B375B]/20 md:col-span-2">
            <p className="text-[10px] text-[#1B375B]">Total collected</p>
            <p className="font-bold text-[#1B375B]">{fmt(cs.total_collected || commercial?.total_amount)}</p>
          </div>
        </div>
        {cs.customer_gstin && (
          <p className="text-xs text-gray-600 mt-2">Customer GSTIN: <span className="font-mono">{cs.customer_gstin}</span></p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <p className="text-[10px] uppercase font-bold text-gray-500 px-4 pt-3">Transaction history</p>
        {entries.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-500">No ledger rows — summary above from booking totals.</p>
        ) : (
          <div className="divide-y divide-gray-100 mt-2">
            {entries.map((e, idx) => {
              const tone = TXN_TONE[e.type] || TXN_TONE.ADJUSTMENT;
              return (
                <div key={idx} className="px-4 py-3 flex flex-wrap items-start justify-between gap-2">
                  <div className="flex-1 min-w-[200px]">
                    <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${tone}`}>
                      {e.type}
                    </span>
                    <p className="font-medium text-gray-900 mt-1">{e.description}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{e.channel} · {fmtIST(e.created_at)}</p>
                    {e.reference && <p className="text-xs font-mono text-gray-600 mt-0.5 break-all">Ref: {e.reference}</p>}
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${e.type === 'REFUNDED' ? 'text-blue-700' : 'text-emerald-700'}`}>
                      {e.type === 'REFUNDED' ? '+' : '−'}{fmt(e.amount)}
                    </p>
                    <p className="text-[10px] text-gray-500 uppercase">{e.status}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
